import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './AppInsights';
import { useRoutes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CssBaseline, ThemeProvider } from '@material-ui/core';
import './i18n';
import RTL from './components/RTL';
import useScrollReset from './hooks/useScrollReset';
import useScrollResetContainer from './hooks/useScrollResetContainer';
import useSettings from './hooks/useSettings';
import routes from './routes';
import { createCustomTheme } from './theme';
import ReactGA from 'react-ga4';
import { TranslationProvider } from './react-auto-google-translate/lib/main.ts';
import React from 'react';

function setAppInsightsUser() {
  const token = localStorage.getItem('accessToken');
  if (token) {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload?.email) {
      if (window.appInsights.context.user.authenticatedId !== payload?.email) {
        window.appInsights.setAuthenticatedUserContext(payload.email);
      }
    }
  } else if (window.appInsights.context.user.authenticatedId !== null) {
    window.appInsights.clearAuthenticatedUserContext();
  }
}

const App = () => {
  const content = useRoutes(routes);
  const { settings, saveSettings } = useSettings();
  const location = useLocation();

  useScrollReset();
  useScrollResetContainer('.dashboardLayoutContent');

  // Add route-based class to body element
  React.useEffect(() => {
    const { pathname } = location;
    const routeClass = pathname.substring(1).replace(/\//g, '-') || 'home';
    document.body.className = `route-${routeClass}`;
  }, [location.pathname]);

  // Check HTML lang attribute and set dir attribute if RTL
  React.useEffect(() => {
    const htmlElement = document.documentElement;

    const setRTLDirection = (lang) => {
      const isRTL = lang && ['ar', 'he', 'fa'].includes(lang);
      const newDirection = isRTL ? 'rtl' : 'ltr';

      // Only update if the direction actually changed
      if (settings.direction !== newDirection) {
        htmlElement.setAttribute('dir', newDirection);
        // Update settings to match the language direction
        saveSettings({
          ...settings,
          direction: newDirection
        });
      }
    };

    // Initial direction setup
    setRTLDirection(htmlElement.getAttribute('lang'));

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
          setRTLDirection(htmlElement.getAttribute('lang'));
        }
      });
    });

    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['lang']
    });

    return () => observer.disconnect();
  }, [settings, saveSettings]);

  const theme = createCustomTheme({
    theme: settings.theme,
    pathname: location.pathname,
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
  });

  setAppInsightsUser();

  ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS);
  ReactGA.send('pageview');

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <ThemeProvider theme={theme}>
        <TranslationProvider originalLang="pt">
          <RTL direction={settings.direction}>
            <CssBaseline />
            <Toaster position="top-center" />
            {content}
          </RTL>
        </TranslationProvider>
      </ThemeProvider>
    </AppInsightsContext.Provider>
  );
};

export default App;
